import { createBrowserRouter } from 'react-router-dom';
import { RouteError } from './RouteError';

/**
 * Full route table, registered up-front and then FROZEN: build phases fill in
 * the lazy targets but never edit this file, so parallel work cannot collide
 * here. Every page is a lazy chunk boundary — the public marketing bundle
 * never pulls app code (or recharts), and vice versa.
 *
 * `RouteError` is the ONE deliberate exception to the lazy rule: it is imported
 * eagerly so it lives in the entry bundle. An error page shipped as its own
 * chunk cannot render when the failure IS a chunk that will not load — which is
 * precisely the stale-deploy case it has to handle.
 *
 * Every top-level branch carries it. Without an `errorElement` React Router
 * falls back to its own developer screen, stack trace included, in production.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteError />,
    lazy: async () => ({
      Component: (await import('./marketing/MarketingLayout')).MarketingLayout,
    }),
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('./marketing/HomePage')).HomePage }),
      },
      {
        path: 'features',
        lazy: async () => ({ Component: (await import('./marketing/FeaturesPage')).FeaturesPage }),
      },
      {
        path: 'pricing',
        lazy: async () => ({ Component: (await import('./marketing/PricingPage')).PricingPage }),
      },
      {
        path: 'trust',
        lazy: async () => ({ Component: (await import('./marketing/TrustPage')).TrustPage }),
      },
      {
        path: 'login',
        lazy: async () => ({ Component: (await import('./marketing/LoginPage')).LoginPage }),
      },
      {
        path: 'login/mfa',
        lazy: async () => ({ Component: (await import('./marketing/MfaPage')).MfaPage }),
      },
      {
        path: 'forgot-password',
        lazy: async () => ({
          Component: (await import('./marketing/ForgotPasswordPage')).ForgotPasswordPage,
        }),
      },
      {
        path: 'request-demo',
        lazy: async () => ({
          Component: (await import('./marketing/RequestDemoPage')).RequestDemoPage,
        }),
      },
      {
        // PUBLIC sign-up page reached from an invite link. Token-gated, not
        // auth-gated: an unauthenticated invitee must be able to reach it.
        path: 'accept-invite/:token',
        lazy: async () => ({
          Component: (await import('./marketing/AcceptInvitePage')).AcceptInvitePage,
        }),
      },
      {
        path: '*',
        lazy: async () => ({ Component: (await import('./marketing/NotFoundPage')).NotFoundPage }),
      },
    ],
  },
  {
    path: '/app',
    errorElement: <RouteError />,
    lazy: async () => ({ Component: (await import('./app/AppShellRoute')).AppShellRoute }),
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (await import('./app/dashboard/DashboardPage')).DashboardPage,
        }),
      },
      {
        // The finances builder IS the client home — creating a client lands
        // here, not on a separate page. (The older fixture-only Workspace and
        // Scenarios views were deleted in D1.4, 2026-09-06; the discrete-plan
        // Report view stays unlinked until it is ported to the real API.)
        // The client WORKSPACE (adviser dashboard redesign 2026-09-13): one
        // owner of the document with four destinations beneath it — Client
        // details, Results, Scenario comparison (`/scenarios`, the URL kept) and Presentation.
        // A splat so the workspace routes its own destinations.
        path: 'clients/:clientId/*',
        lazy: async () => ({
          Component: (await import('./app/builder/BuilderPage')).BuilderPage,
        }),
      },
      {
        // A COMPLETED review, rendered from the calculation it was completed
        // against (GOAL 2026-09-10 item 1). Read-only by construction.
        path: 'clients/:clientId/reviews/:reviewId',
        lazy: async () => ({
          Component: (await import('./app/review/CompletedReviewPage')).CompletedReviewPage,
        }),
      },
      {
        // The tax-rules register: which year's rules are active, every figure
        // in plain English, review status and sources. Available to every
        // adviser role — it shows product-wide rule data, never client data.
        path: 'tax-rules',
        lazy: async () => ({
          Component: (await import('./app/taxrules/TaxRulesPage')).TaxRulesPage,
        }),
      },
      {
        // Owner-only firm administration. The page itself gates to `owner`
        // (RequireRole) and shows a plain explanation to anyone else.
        path: 'team',
        lazy: async () => ({ Component: (await import('./app/team/TeamPage')).TeamPage }),
      },
      {
        // Owner-only firm modelling assumptions (Firm settings tab).
        path: 'firm-settings',
        lazy: async () => ({
          Component: (await import('./app/firm/FirmSettingsPage')).FirmSettingsPage,
        }),
      },
      {
        // This adviser's OWN arrangement of the app — no role gate, because
        // everyone owns their own settings and nobody owns anyone else's.
        path: 'settings',
        lazy: async () => ({
          Component: (await import('./app/account/PersonalSettingsPage')).PersonalSettingsPage,
        }),
      },
    ],
  },
  {
    path: '/app/clients/:clientId/report',
    errorElement: <RouteError />,
    lazy: async () => ({ Component: (await import('./report/ReportRoute')).ReportRoute }),
  },
  {
    // The Live Projection on its own tab (TV screen / second monitor) —
    // outside the app chrome like the report, auth-gated, follows the
    // builder over a same-origin BroadcastChannel.
    path: '/app/clients/:clientId/projection',
    errorElement: <RouteError />,
    lazy: async () => ({
      Component: (await import('./app/builder/ProjectionWindowRoute')).ProjectionWindowRoute,
    }),
  },
]);
